/** Member-side marketing brief: budget / areas / goals so the team can start ads straight away. */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Megaphone, Pencil, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { wget, wpost, wput } from '@/components/website/api';
import { EMPTY_ANSWERS, OnboardingQuestions } from '@/components/marketing/OnboardingQuestions';

const STATUS_COPY = {
  submitted: { title: 'Brief received — thanks!', body: "The team has your answers and will be in touch to get your ads live. You'll get a notification when everything's set up." },
  in_progress: { title: "We're setting up your marketing", body: 'Campaigns are being built from your brief. Shout if anything changes.' },
  set_up: { title: 'Your marketing is live', body: 'Keep an eye on the Leads board — new enquiries land there first.' },
};

export default function MarketingOnboarding() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [a, setA] = useState(EMPTY_ANSWERS);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    wget('/marketing/onboarding/me').then((d) => {
      setData(d);
      const ans = d.doc?.answers || {};
      setA({ ...EMPTY_ANSWERS, ...ans, areas: ans.areas?.length ? ans.areas : d.prefill?.postcode ? [d.prefill.postcode] : [], website: ans.website || d.prefill?.website || '' });
    }).catch((e) => toast.error(e.message));
  }, []);

  const set = (patch) => {
    setA((prev) => {
      const next = { ...prev, ...patch };
      if (!data?.doc?.submitted_at) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => wput('/marketing/onboarding/me', { answers: next }).catch(() => {}), 800);
      }
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const d = await wpost('/marketing/onboarding/me/submit', { answers: a });
      setData((prev) => ({ ...prev, doc: d.doc }));
      setEditing(false);
      toast.success("Brief sent — the team will take it from here");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#006400]" /></div>;
  const doc = data.doc;
  const submitted = !!doc?.submitted_at && !editing;
  const copy = STATUS_COPY[doc?.status] || STATUS_COPY.submitted;

  return (
    <div className="min-h-screen bg-[#E8ECEF]" data-testid="marketing-onboarding-page">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        <button type="button" onClick={() => navigate('/')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="mo-back"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#006400] text-white flex items-center justify-center shrink-0"><Megaphone className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Let's get your marketing started</h1>
            <p className="text-sm md:text-base text-slate-600 mt-1">Six quick questions so the team can build your campaigns straight away. Takes about two minutes.</p>
          </div>
        </div>

        {!data.required && !doc && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 text-sm text-slate-700" data-testid="mo-not-required">
            This brief is for <strong>CRM + Marketing</strong> members. Upgrade on the <button type="button" className="text-[#006400] font-semibold underline" onClick={() => navigate('/billing')}>plan page</button> and we'll run your ads for you.
          </div>
        )}

        {submitted && (
          <div className="rounded-2xl bg-white border-2 border-[#006400]/30 p-6 mb-4" data-testid="mo-submitted">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-7 h-7 text-[#006400] shrink-0" />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-slate-900" data-testid="mo-status-title">{copy.title}</h2>
                <p className="text-sm text-slate-600 mt-1">{copy.body}</p>
                <ul className="mt-4 space-y-1 text-sm text-slate-800" data-testid="mo-summary">
                  {(data.summary || summaryOf(a, data.options)).map((l) => <li key={l} className="flex gap-2"><span className="text-[#006400]">•</span><span>{l}</span></li>)}
                </ul>
                <Button variant="outline" className="mt-5" onClick={() => setEditing(true)} data-testid="mo-edit"><Pencil className="w-4 h-4 mr-1" /> Update my answers</Button>
              </div>
            </div>
          </div>
        )}

        {(data.required || doc) && !submitted && (
          <>
            <OnboardingQuestions a={a} set={set} options={data.options} prefill={data.prefill} />
            <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-3 bg-[#E8ECEF]/90 backdrop-blur border-t border-slate-200 flex items-center gap-3 flex-wrap">
              <p className="text-xs text-slate-500 flex-1 min-w-[200px]">{doc?.submitted_at ? 'Your changes replace the brief the team already has.' : 'Your answers save as you go — finish any time.'}</p>
              {editing && <Button variant="outline" onClick={() => setEditing(false)} data-testid="mo-cancel-edit">Cancel</Button>}
              <Button onClick={submit} disabled={busy} className="bg-[#006400] hover:bg-[#004d00] text-white h-11 px-6" data-testid="mo-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}{doc?.submitted_at ? 'Send updated brief' : 'Send my brief to the team'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function summaryOf(a, o = {}) {
  const budget = a.budget === 'custom' && a.budget_custom ? a.budget_custom : (o.budgets || {})[a.budget] || '—';
  const goals = (a.goals || []).map((g) => (g === 'other' && a.goals_other ? a.goals_other : (o.goals || {})[g] || g));
  return [
    `Budget: ${budget}`,
    `Areas: ${(a.areas || []).join(', ') || '—'}`,
    `Goals: ${goals.join(', ') || '—'}`,
    `Services: ${(a.services || []).map((s) => (o.services || {})[s] || s).join(', ') || '—'}`,
    `Ad accounts: ${(a.ad_accounts || []).map((s) => (o.ad_accounts || {})[s] || s).join(', ') || '—'}${a.facebook_page ? ` · FB: ${a.facebook_page}` : ''}`,
    `Best time to call: ${(o.contact_times || {})[a.contact_time] || 'Any time'}`,
    ...(a.notes ? [`Notes: ${a.notes}`] : []),
  ];
}
